import { Chess } from 'chess.js';

import type { StockfishPrincipalVariation, StockfishScore } from '../engine';

import {
  deriveAiSeed,
  nextDeterministicRandom,
  randomUnitInterval,
} from './deterministic';

const UCI_PATTERN = /^[a-h][1-8][a-h][1-8][qrbn]?$/;

/**
 * Keep Curious Capy visibly varied without accepting a materially weaker line.
 * Engine centipawn scores within this bounded window are eligible to sample.
 */
export const STOCKFISH_MULTIPV_QUALITY_WINDOW_CP = 50;

export interface StockfishMoveSelectionInput {
  readonly bestMove: string | null;
  readonly principalVariations: readonly StockfishPrincipalVariation[];
  readonly legalMoves: readonly string[] | ReadonlySet<string>;
  readonly gameSeed: string;
  readonly tierVersion: string;
  readonly engineBuild: string;
  readonly positionFen: string;
  readonly ply: number;
}

export interface StockfishMoveSelection {
  readonly uci: string | null;
  readonly source: 'multipv' | 'bestmove' | 'none';
  readonly candidateCount: number;
}

interface ScoredCandidate {
  readonly uci: string;
  readonly quality: number;
  readonly multiPv: number;
}

/**
 * Pure selection over a completed MultiPV result. It never mutates engine or
 * game state; the bridge remains responsible for the final live legality gate.
 */
export function selectStockfishMove(
  input: StockfishMoveSelectionInput,
): StockfishMoveSelection {
  const fallback = validLegalMove(input.bestMove, input.legalMoves) ? input.bestMove : null;
  const candidates = uniqueSortedCandidates(input.principalVariations, input.legalMoves);
  const best = candidates[0];
  if (!best) {
    return fallbackSelection(fallback, 0);
  }

  const viable = candidates.filter(
    (candidate) => candidate.quality >= best.quality - STOCKFISH_MULTIPV_QUALITY_WINDOW_CP,
  );
  const position = normalizedPosition(input.positionFen);
  if (viable.length === 0 || position === null) {
    return fallbackSelection(fallback, viable.length);
  }

  const seed = deriveAiSeed({
    gameSeed: input.gameSeed,
    tierVersion: input.tierVersion,
    engineBuild: input.engineBuild,
    positionFen: position.fen,
    positionHash: position.hash,
    ply: input.ply,
  });
  const draw = randomUnitInterval(nextDeterministicRandom(seed).value);
  const selected = viable[Math.floor(draw * viable.length)];
  if (!selected) {
    return fallbackSelection(fallback, viable.length);
  }

  return Object.freeze({
    uci: selected.uci,
    source: 'multipv' as const,
    candidateCount: viable.length,
  });
}

/** Converts Stockfish scores onto a common, higher-is-better centipawn scale. */
export function normalizeStockfishScore(score: StockfishScore): number | null {
  if (!Number.isFinite(score.value)) {
    return null;
  }
  if (score.kind === 'cp') {
    return score.value;
  }
  return score.value > 0
    ? 1_000_000 - Math.abs(score.value)
    : -1_000_000 + Math.abs(score.value);
}

function uniqueSortedCandidates(
  principalVariations: readonly StockfishPrincipalVariation[],
  legalMoves: readonly string[] | ReadonlySet<string>,
): readonly ScoredCandidate[] {
  const sorted = principalVariations
    .flatMap((principalVariation): readonly ScoredCandidate[] => {
      const uci = principalVariation.moves[0];
      const score = principalVariation.score;
      if (
        uci === undefined ||
        !UCI_PATTERN.test(uci) ||
        !containsLegalMove(legalMoves, uci) ||
        score === null ||
        score.bound !== undefined
      ) {
        return [];
      }
      const quality = normalizeStockfishScore(score);
      return quality === null
        ? []
        : [{ uci, quality, multiPv: principalVariation.multiPv }];
    })
    .sort(compareCandidates);

  const seen = new Set<string>();
  return Object.freeze(sorted.filter((candidate) => {
    if (seen.has(candidate.uci)) {
      return false;
    }
    seen.add(candidate.uci);
    return true;
  }));
}

function compareCandidates(left: ScoredCandidate, right: ScoredCandidate): number {
  if (left.quality !== right.quality) {
    return right.quality - left.quality;
  }
  if (left.uci !== right.uci) {
    return left.uci < right.uci ? -1 : 1;
  }
  return left.multiPv - right.multiPv;
}

function normalizedPosition(positionFen: string): { readonly fen: string; readonly hash: string } | null {
  try {
    const chess = new Chess(positionFen);
    return Object.freeze({ fen: chess.fen(), hash: chess.hash() });
  } catch {
    return null;
  }
}

function validLegalMove(
  uci: string | null,
  legalMoves: readonly string[] | ReadonlySet<string>,
): uci is string {
  return uci !== null && UCI_PATTERN.test(uci) && containsLegalMove(legalMoves, uci);
}

function containsLegalMove(
  legalMoves: readonly string[] | ReadonlySet<string>,
  uci: string,
): boolean {
  return Array.isArray(legalMoves)
    ? legalMoves.includes(uci)
    : (legalMoves as ReadonlySet<string>).has(uci);
}

function fallbackSelection(fallback: string | null, candidateCount: number): StockfishMoveSelection {
  return Object.freeze({
    uci: fallback,
    source: fallback === null ? 'none' as const : 'bestmove' as const,
    candidateCount,
  });
}
